const STORAGE_PREFIX = "kb_session_selection:";

const normalizeFilename = (value) => String(value ?? "").trim();

export const normalizeKbFileEntry = (value) => {
  if (typeof value === "string") {
    const filename = normalizeFilename(value);
    return filename ? { filename, document_id: null } : null;
  }

  if (value && typeof value === "object") {
    const filename = normalizeFilename(value.filename ?? value.name);
    if (!filename) return null;

    const rawDocId = value.document_id ?? value.documentId ?? null;
    const document_id =
      rawDocId == null || String(rawDocId).trim() === ""
        ? null
        : String(rawDocId);

    return { filename, document_id };
  }

  return null;
};

export const normalizeKbContextEntry = (value) => {
  if (!value || typeof value !== "object") return null;

  const type = normalizeFilename(value.type).toLowerCase();
  if (type !== "folder") return null;

  const folder_id = normalizeFilename(
    value.folder_id ?? value.folderId ?? value.id,
  );
  if (!folder_id) return null;

  return {
    ...value,
    type: "folder",
    folder_id,
  };
};

export const normalizeKbSelectionSnapshot = (value) => {
  if (!value || typeof value !== "object") return null;

  const selectedFiles = Array.isArray(value.selectedFiles)
    ? value.selectedFiles
        .map(normalizeKbFileEntry)
        .filter(Boolean)
    : [];
  const resolvedKnowledgeBaseFiles = Array.isArray(
    value.resolvedKnowledgeBaseFiles,
  )
    ? value.resolvedKnowledgeBaseFiles
        .map(normalizeKbFileEntry)
        .filter(Boolean)
    : [];
  const selectedKnowledgeContexts = Array.isArray(
    value.selectedKnowledgeContexts,
  )
    ? value.selectedKnowledgeContexts
        .map(normalizeKbContextEntry)
        .filter(Boolean)
    : [];
  const selectedGroups = Array.isArray(value.selectedGroups)
    ? value.selectedGroups.map((groupId) => normalizeFilename(groupId)).filter(Boolean)
    : [];
  const knowledgeContextSummary =
    value.knowledgeContextSummary &&
    typeof value.knowledgeContextSummary === "object"
      ? value.knowledgeContextSummary
      : {};

  return {
    selectedFiles,
    resolvedKnowledgeBaseFiles,
    selectedKnowledgeContexts,
    selectedGroups,
    knowledgeContextSummary,
    savedAt: Number(value.savedAt) || Date.now(),
  };
};

export const getUniqueKbDocumentIdCount = ({
  selectedFiles = [],
  selectedKnowledgeContexts = [],
  knowledgeContextSummary = {},
} = {}) => {
  const ids = new Set();
  const addEntry = (entry) => {
    const docId = normalizeFilename(entry?.document_id);
    if (docId) ids.add(docId);
  };

  (Array.isArray(selectedFiles) ? selectedFiles : []).forEach(addEntry);

  const explicitCount = ids.size;

  const folderCountFromContexts = (Array.isArray(selectedKnowledgeContexts)
    ? selectedKnowledgeContexts
    : []
  ).reduce((sum, ctx) => {
    if (ctx?.type !== "folder") return sum;
    const count = Number(ctx.file_count);
    return Number.isFinite(count) && count > 0 ? sum + count : sum;
  }, 0);

  const fallbackSummaryCount = Number(knowledgeContextSummary?.file_count);
  if (folderCountFromContexts > 0) return explicitCount + folderCountFromContexts;
  if (Number.isFinite(fallbackSummaryCount) && fallbackSummaryCount > 0) {
    return explicitCount + fallbackSummaryCount;
  }

  return explicitCount;
};

export const saveKbSessionSelection = (sessionId, selection = {}) => {
  const key = String(sessionId || "").trim();
  if (!key || typeof window === "undefined" || !window.localStorage) return;

  const snapshot = normalizeKbSelectionSnapshot(selection);
  if (!snapshot) return;

  try {
    window.localStorage.setItem(
      `${STORAGE_PREFIX}${key}`,
      JSON.stringify(snapshot),
    );
  } catch {}
};

export const readKbSessionSelection = (sessionId) => {
  const key = String(sessionId || "").trim();
  if (!key || typeof window === "undefined" || !window.localStorage) return null;

  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (!raw) return null;
    return normalizeKbSelectionSnapshot(JSON.parse(raw));
  } catch {
    return null;
  }
};
